import {useContext, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import AtomButton, {atomButtonColors, atomButtonDesigns, atomButtonShapes} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import MoleculeCheckboxField, {moleculeCheckboxFieldSizes} from '@s-ui/react-molecule-checkbox-field'
import MoleculeInputField from '@s-ui/react-molecule-input-field'
import IconUnchecked from '@adv-mt/mt-svg-icons/lib/CheckboxEmpty.js'
import IconChecked from '@adv-mt/mt-svg-icons/lib/CheckboxFilled.js'
import TitleBasic, {titleBasicColors, titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import {isValidEmail} from './isValidEmail.js'
import {Subscribed} from './subscribed.js'

const BASE_CLASS = 'mt-NewsletterForm'
const FORM_CLASS = `${BASE_CLASS}-form`
const FORM_CONTENT_CLASS = `${FORM_CLASS}Content`
const LINK_CLASS = `${BASE_CLASS}-link`
const SUBMIT_BUTTON_CLASS = `${BASE_CLASS}-submit`

const NO_OP = () => {}

const getEmail = ({user}) => user?.email || ''

const NewsletterForm = ({consents, hasCheckbox = true, isLarge, onAfterSubscribe = NO_OP, pageName, user}) => {
  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')
  const {track} = useAnalyticsTrack()

  const [email, setEmail] = useState(getEmail({user}))
  const [isChecked, setIsChecked] = useState(!hasCheckbox)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const isValidEmailTyped = isValidEmail({config, email})
  const linkDataProtection = config.get('page').DATA_PROTECTION_PATH_PATTERN

  const TENANT = config.get('TENANT')

  const emailErrorText = i18n.t('FORM.EMAIL_TEXT_ERROR')
  const termsAndConditionsErrorText = i18n.t('FORM.LEGAL_TEXT_ERROR')
  const linkConditions = i18n.t('URL_COCHES_CONDITIONS')
  const inputPlaceholder = i18n.t('FORM.EMAIL_PLACEHOLDER_EXAMPLE')
  const subscribeCtaText = i18n.t(`NEWSLETTER_SUBSCRIBE_CTA_${TENANT.toUpperCase()}`)
  const titleText = i18n.t(`NEWSLETTER_SUBSCRIBE_TITLE_${TENANT.toUpperCase()}`)

  const labelElementTermsAndConditions = (
    <label
      className={`${BASE_CLASS}-termsAndConditions`}
      dangerouslySetInnerHTML={{
        __html: i18n.t('FORM.LEGAL_CHECKBOX_LABEL', {
          linkStartConditions: `<a href="${linkConditions}" class=${LINK_CLASS} target='_blank'>`,
          linkEndConditions: '</a>',
          linkStartPrivacy: `<a href="${linkDataProtection}" class=${LINK_CLASS} target='_blank'>`,
          linkEndPrivacy: '</a>'
        })
      }}
    />
  )

  const trackSubscribeSuccess = async () => {
    const {NEWSLETTER_CLICK} = config.get('tracking').TRACKING_EVENT_NAMES

    const trackingData = await domain
      .get('get_event_tracking_data_use_case')
      .execute({event: NEWSLETTER_CLICK, pageName})

    track(trackingData)
  }

  const onSubscribe = () => {
    domain
      .get('subscribe_editorial_use_case')
      .execute({email, pageName})
      .then(([_, response]) => {
        if (response) {
          setIsSubscribed(true)
          trackSubscribeSuccess()
          onAfterSubscribe()
        }
      })
      .catch(() => {
        setIsLoading(false)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const onSubscribeWithConsents = async () => {
    const {USER_CONSENTS} = domain.get('config').get('user')

    consents.setConsentStatus({
      key: USER_CONSENTS.PORTAL_ANNOUNCEMENTS,
      status: true
    })

    const [getUpdateConsentsError, getUpdateConsentsResponse] = await domain
      .get('update_consents_user_use_case')
      .execute(consents)

    if (getUpdateConsentsResponse) {
      setIsSubscribed(true)
      trackSubscribeSuccess()
      onAfterSubscribe()
    }
    if (getUpdateConsentsError) console.error(getUpdateConsentsError)

    setIsLoading(false)
  }

  const handleInputChange = ({target: {value}}) => setEmail(value)
  const handleCheckboxChange = () => setIsChecked(!isChecked)

  const handleSubmit = e => {
    e.preventDefault()
    setIsSubmitted(true)
    if (isValidEmailTyped && isChecked) {
      setIsLoading(true)
      onSubscribe()
    }
  }

  const handleSubmitWithConsents = e => {
    e.preventDefault()
    setIsSubmitted(true)
    if (isValidEmailTyped && isChecked) {
      setIsLoading(true)
      onSubscribeWithConsents()
    }
  }

  const emailErrorProp = isSubmitted && !isValidEmailTyped && emailErrorText
  const termsAndConditionsErrorProp = isSubmitted && !isChecked && termsAndConditionsErrorText

  const formClassName = cx(FORM_CLASS, {
    [`${FORM_CLASS}--large`]: isLarge
  })

  return (
    <div className={BASE_CLASS}>
      {isSubscribed && !consents ? (
        <Subscribed />
      ) : (
        <>
          <TitleBasic
            as={titleBasicTags.h3}
            color={titleBasicColors.black}
            size={titleBasicSizes.s}
            title={titleText}
          />
          <form className={formClassName} onSubmit={consents ? handleSubmitWithConsents : handleSubmit}>
            <div className={FORM_CONTENT_CLASS}>
              <MoleculeInputField
                id="newsletterEmailInput"
                errorText={emailErrorProp}
                placeholder={inputPlaceholder}
                type="email"
                onChange={user ? NO_OP : handleInputChange}
                value={email}
                disabled={Boolean(user)}
              />
              {hasCheckbox && (
                <MoleculeCheckboxField
                  id="newsletterTerms"
                  checked={isChecked}
                  checkedIcon={IconChecked}
                  uncheckedIcon={IconUnchecked}
                  nodeLabel={labelElementTermsAndConditions}
                  onChange={handleCheckboxChange}
                  size={moleculeCheckboxFieldSizes.MEDIUM}
                  errorText={termsAndConditionsErrorProp}
                />
              )}
            </div>

            <div className={SUBMIT_BUTTON_CLASS}>
              <AtomButton
                design={atomButtonDesigns.SOLID}
                shape={atomButtonShapes.CIRCULAR}
                color={atomButtonColors.ACCENT}
                isLoading={isLoading}
                isSubmit
              >
                {subscribeCtaText}
              </AtomButton>
            </div>
          </form>
        </>
      )}
    </div>
  )
}

NewsletterForm.displayName = 'NewsletterForm'
NewsletterForm.propTypes = {
  consents: PropTypes.object,
  hasCheckbox: PropTypes.bool,
  isLarge: PropTypes.bool,
  onAfterSubscribe: PropTypes.func,
  pageName: PropTypes.string,
  user: PropTypes.object
}

export default NewsletterForm
